<template>
  <div class="d-flex flex-column">
    <div class="grid-header">
      <h1>{{ $t("agentes_inmobiliaria.titulo") }}</h1>
      <div class="toolbar">
        <button type="button" @click.prevent="crearAgenteInmobiliaria()" class="btn btn-link">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
    </div>

    <DxDataGrid v-bind="getDefaultGridProperties()" :data-source="gridSource" ref="grid"
      class="dx-card wide-card grid-agentes-inmobiliaria">
      <DxColumnFixing :enabled="true" />
      <DxColumn data-field="NombreUsuario" :caption="$t('agentes_inmobiliaria.grid.nombre')"></DxColumn>
      <DxColumn data-field="ApellidosUsuario" :caption="$t('agentes_inmobiliaria.grid.apellidos')"></DxColumn>
      <DxColumn data-field="EmailUsuario" :caption="$t('agentes_inmobiliaria.grid.email')"></DxColumn>
      <DxColumn data-field="DocumentoUsuario" :caption="$t('agentes_inmobiliaria.grid.dni_nie')"></DxColumn>
      <DxColumn data-field="TelefonoUsuario" :caption="$t('agentes_inmobiliaria.grid.telefono')"></DxColumn>
      <DxColumn data-field="DireccionUsuario" :caption="$t('agentes_inmobiliaria.grid.direccion')"></DxColumn>
      <DxColumn data-field="NombresDelegacionInmobiliaria"
        :caption="$t('agentes_inmobiliaria.grid.nombre_delegacion_inmobiliaria')" cell-template="delegacionesTemplate">
      </DxColumn>
      <DxColumn data-field="CodigoCometidoAgente" :caption="$t('agentes_inmobiliaria.grid.codigo_cometido')"
        cell-template="cometidosTemplate" >
        <DxLookup v-bind="{ ...getLookup('agentesInmobiliaria', 'cometido') }"></DxLookup>
      </DxColumn>

      <DxColumn type="buttons" :caption="$t('agentes_inmobiliaria.grid.acciones')" :fixed="true"
        fixed-position="right" :buttons="[
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-edit text-success',
            hint: $t('agentes_inmobiliaria.grid.editar'),
            onClick: (e: any) => verDetalle(e.row.data.IdUsuario),
            disabled: (e: any) => !puedeEditar(e.row?.data)
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-trash text-success',
            hint: $t('agentes_inmobiliaria.grid.borrar'),
            onClick: (e: any) => borrar(e.row.data.IdUsuario),
            disabled: (options: any) => !puedeBorrar(options.row?.data)
          }
        ]"/> 

      <template #cometidosTemplate="{ data }">
        <div class="cometidos">
          <span v-for="(cometido, index) in (data.value || '').split(';').map((cometido: string) => cometido.trim())"
            :key="cometido" class="d-block rounded py-1 px-2 mr-2" :class="`rol ${cometido}`">
            <p class="text-center mb-0">
              {{ $t(`agentes_inmobiliaria.grid.${cometido}`) }}
            </p>
          </span>
        </div>
      </template>

      <template #delegacionesTemplate="{ data }">
        <div v-if="data.value" class="delegaciones">
          <template v-for="(delegacion, index) in (data.value || '').split(';').map((d: string) => d.trim())" :key="index">
            <span v-if="delegacion !== ''" class="d-block rounded py-1 px-2 delegacion">
              {{ delegacion }}
            </span>
          </template>
        </div>
      </template>
    </DxDataGrid>
  </div>
</template>


<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import { useIntl } from "@services/i18n";
import { useApi } from "@api/integration";
import { CometidoAgenteInmobiliariaEnum, type DXLoadOptions } from "@/.shared/.api";
import { useModalService } from "@services/modal-service";
import type CustomStore from "devextreme/data/custom_store";
import { messageBoxResult } from '@components/dialogs/message-box';
import { DxDataGrid, DxColumn, DxColumnFixing, DxLookup } from "devextreme-vue/data-grid";
import { getDefaultGridProperties, getLookup } from "@helpers/grid-helper";
import AgenteInmobiliariaForm from "@/components/agentes/AgenteInmobiliariaForm.vue";
import { CreateAgenteInmobiliaria } from "@/components/agentes/models/CreateAgenteInmobiliaria";
import { useAuthorization } from "@/.shared/services/auth/authorization/authorization-service-impl";
import { PermissionsApp } from "@/.shared/services/auth/authorization/authorization-service-impl";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import { DxButton } from "devextreme-vue";
import { useAuthentication, ExtendedUserInfo } from "@/.shared/services/auth";

const api = useApi();
const intl = useIntl();
const modal = useModalService();
const auth = useAuthorization();
const authentication = useAuthentication();

const grid = ref<DxDataGrid>();
const gridSource = ref<CustomStore>();

const currentUserId = computed(() => (authentication.userInfo as ExtendedUserInfo)?.idUsuario);

const isDirector = computed(() => auth.hasPermission(PermissionsApp.EditSameInmobiliariaAgente));
const isCoordinadorDelegacion = computed(() => auth.hasPermission(PermissionsApp.EditSameDelegacionAgente) && !isDirector.value);

onMounted(async () => {
  setDataSource();
});

const setDataSource = async () => {
  gridSource.value = api.apiStore({
    key: "IdUsuario",
    load: async (p) => {
      const q = p as DXLoadOptions;
      return await api.callStandardApi("getAgentesInmobiliariaForGrid", { showLoading: false }, q);
    }
  });
}

async function crearAgenteInmobiliaria() {
  let title = `${intl.i18n.t("agentes_inmobiliaria.creacion.titulo")}`;
  modal.showModal(
    AgenteInmobiliariaForm,
    { model: new CreateAgenteInmobiliaria(), isEditMode: false },
    { title: title, width: 900 }
  ).then((value) => {
    if (value == messageBoxResult.ok)
      grid.value?.instance?.refresh();
  });
}

const verDetalle = async (idAgenteInmobiliaria: string) => {
  let title = `${intl.i18n.t("agentes_inmobiliaria.edicion.titulo")}`;
  let agenteInmobiliaria = await api.callStandardApi("getAgenteInmobiliariaById", { showLoading: true }, idAgenteInmobiliaria);
  modal.showModal(
    AgenteInmobiliariaForm,
    { model: agenteInmobiliaria, isEditMode: true },
    { title: title, width: 900, height: 700 }
  ).then((value) => {
    if (value == messageBoxResult.ok)
      grid.value?.instance?.refresh();
  });
}

const borrar = async (idAgenteInmobiliaria: string) => {
  let respuesta = await modal.messageBox(intl.i18n.t("agentes_inmobiliaria.grid.borrar_pregunta"), intl.i18n.t("agentes_inmobiliaria.grid.titulo_confirma_borrar"), "okCancel", "warning");
  if (respuesta == messageBoxResult.ok)
    await api.callStandardApi("deleteAgenteInmobiliaria", { showLoading: true }, idAgenteInmobiliaria)
      .then(() => {
        toastSuccess(intl.i18n.t("agentes.borrado_exito"));
        grid.value?.instance?.refresh();
      })
      .catch(() => toastError(intl.i18n.t("agentes.borrado_fallo")));
};

const puedeEditar = (agenteInmobiliaria: any) => {
  if (isDirector.value) 
    return true;

  if (isCoordinadorDelegacion.value) 
    return agenteInmobiliaria.CodigoCometidoAgente === CometidoAgenteInmobiliariaEnum.Agente;

  return false;
};

const puedeBorrar = (agenteInmobiliaria: any) => {
  if (!puedeEditar(agenteInmobiliaria) || agenteInmobiliaria.FechaBaja != null) 
    return false;

  if (agenteInmobiliaria.IdUsuario === currentUserId.value) 
    return false;

  return true;
};
</script>


<style lang="scss" scoped>


.cometidos span:nth-child(n + 2) {
  margin-top: 5px;
}


.delegaciones span:nth-child(n + 2) {
  margin-top: 5px;
}

.delegacion {
  color: var(--text-ui-tag-amarillo);
  background-color: var(--background-ui-tag-amarillo) !important;
  border: 1px solid var(--border-ui-tag-amarillo) !important;
}
</style>